import { schedule } from "@ember/runloop";
import FullscreenTableModal from "discourse/components/modal/fullscreen-table";
import Columns from "discourse/lib/columns";
import highlightSyntax from "discourse/lib/highlight-syntax";
import { iconElement, iconHTML } from "discourse/lib/icon-library";
import setupImageGridCarousel from "discourse/lib/image-grid-carousel";
import { nativeLazyLoading } from "discourse/lib/lazy-load-images";
import lightbox from "discourse/lib/lightbox";
import { withPluginApi } from "discourse/lib/plugin-api";
import { setTextDirections } from "discourse/lib/text-direction";
import { i18n } from "discourse-i18n";

export default {
  initialize(owner) {
    withPluginApi((api) => {
      const siteSettings = owner.lookup("service:site-settings");
      const session = owner.lookup("service:session");
      const site = owner.lookup("service:site");
      const capabilities = owner.lookup("service:capabilities");
      const modal = owner.lookup("service:modal");

      api.decorateCookedElement((elem) => {
        return highlightSyntax(elem, siteSettings, session);
      });

      api.decorateCookedElement((elem, helper) => {
        const grids = elem.querySelectorAll(".d-image-grid");
        let needsLightboxAfterRender = false;

        if (grids.length) {
          grids.forEach((grid) => {
            if (grid.dataset.mode === "carousel") {
              if (setupImageGridCarousel(grid, helper)) {
                needsLightboxAfterRender = true;
              }
              return;
            }

            return new Columns(grid, {
              columns: site.mobileView ? 2 : 3,
            });
          });
        }

        if (needsLightboxAfterRender) {
          schedule("afterRender", () => {
            lightbox(elem, { post: helper.model });
          });
          return;
        }

        return lightbox(elem, { post: helper.model });
      });

      if (siteSettings.support_mixed_text_direction) {
        api.decorateCookedElement(setTextDirections, {});
      }

      nativeLazyLoading(api);

      api.decorateCookedElement((elem) => {
        elem.querySelectorAll("audio").forEach((player) => {
          player.addEventListener("play", () => {
            const postId = parseInt(
              elem.closest("article")?.dataset.postId,
              10
            );
            if (postId) {
              api.preventCloak(postId);
            }
          });
        });
      });

      const oneboxTypes = {
        amazon: "discourse-amazon",
        githubactions: "fab-github",
        githubblob: "fab-github",
        githubcommit: "fab-github",
        githubpullrequest: "fab-github",
        githubissue: "fab-github",
        githubfile: "fab-github",
        githubgist: "fab-github",
        twitterstatus: "fab-twitter",
        wikipedia: "fab-wikipedia-w",
      };

      api.decorateCookedElement((elem) => {
        elem.querySelectorAll(".onebox").forEach((onebox) => {
          Object.entries(oneboxTypes).forEach(([key, value]) => {
            if (onebox.classList.contains(key)) {
              onebox
                .querySelector(".source")
                .insertAdjacentHTML("afterbegin", iconHTML(value));
            }
          });
        });
      });

      function _createButton(props) {
        const openPopupBtn = document.createElement("button");
        const defaultClasses = [
          "open-popup-link",
          "btn-default",
          "btn",
          "btn-icon",
          ...(props.label ? [] : ["no-text"]),
        ];

        openPopupBtn.classList.add(...defaultClasses);

        if (props.classes) {
          openPopupBtn.classList.add(...props.classes);
        }

        if (props.title) {
          openPopupBtn.title = i18n(props.title);
        }

        if (props.label && capabilities.touch) {
          openPopupBtn.innerHTML = `
          <span class="d-button-label">
            ${i18n(props.label)}
          </div>`;
        }

        if (props.icon) {
          const icon = iconElement(props.icon.name, {
            class: props.icon?.class,
          });
          openPopupBtn.prepend(icon);
        }

        return openPopupBtn;
      }

      function isOverflown({ clientWidth, scrollWidth }) {
        return scrollWidth > clientWidth;
      }

      function generateFullScreenTableModal(event) {
        const { postId } = this;
        const table = event.currentTarget.parentElement.nextElementSibling;

        if (!table) {
          return;
        }

        modal.show(FullscreenTableModal, {
          model: {
            table,
            postId,
            displayFootnotesInline: siteSettings.display_footnotes_inline,
          },
        });
      }

      function generatePopups(tables, post) {
        tables.forEach((table) => {
          if (
            table.parentNode.querySelector(".fullscreen-table-wrapper__buttons")
          ) {
            return;
          }

          table.parentNode.classList.add("fullscreen-table-wrapper");

          if (site.mobileView || !isOverflown(table.parentNode)) {
            return;
          }

          const buttonWrapper = document.createElement("div");
          buttonWrapper.classList.add("fullscreen-table-wrapper__buttons");

          table.parentNode.classList.add("--has-overflow");

          const expandTableBtn = _createButton({
            classes: ["btn-expand-table"],
            title: "fullscreen_table.expand_btn",
            icon: { name: "discourse-expand", class: "expand-table-icon" },
          });
          buttonWrapper.append(expandTableBtn);
          expandTableBtn.addEventListener(
            "click",
            generateFullScreenTableModal.bind({ postId: post.id }),
            false
          );
          table.parentNode.insertBefore(buttonWrapper, table);
        });
      }

      api.decorateCookedElement(
        (element, helper) => {
          schedule("afterRender", () => {
            const tables = element.querySelectorAll(".md-table table");
            generatePopups(tables, helper.model);
          });
        },
        {
          onlyStream: true,
          id: "table-wrapper",
        }
      );
    });
  },
};
